import React, { useEffect, useMemo, useState } from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { ChatMessage } from '../../hooks/useChat';
import type { MessageMenuTrigger } from '../../hooks/useChatUIInteractions';
import { MessageBubble } from './MessageBubble';
import { ImageGroup } from './ImageGroup';
import { FormatTime } from './FormatTime';
import { getMessageCaption } from './useMessageCaption';
import { stableMessageKey } from '../../hooks/chatSync.helpers';

interface MessageListItem {
    type: 'date' | 'message';
    date?: string;
    message?: ChatMessage;
}

interface MessageListProps {
    messages: MessageListItem[];
    conversationName: string;
    conversationAvatar?: string;
    myAvatar?: string;
    chatPlatform?: 'whatsapp' | 'instagram';
    isLoading?: boolean;
    isPopup?: boolean;
    scrollContainerRef: React.RefObject<HTMLDivElement>;
    messagesEndRef: React.RefObject<HTMLDivElement>;
    onScroll: () => void;
    onScrollToMessage: (messageId: string) => void;
    onContextMenu: (e: MessageMenuTrigger, message: ChatMessage) => void;
    highlightedMessageId?: string | null;
    isLoadingOlder?: boolean;
    hasMoreMessages?: boolean;
}

type RenderItem =
    | { kind: 'date'; date: string; key: string }
    | { kind: 'message'; message: ChatMessage; key: string }
    | { kind: 'image-group'; messages: ChatMessage[]; isMe: boolean; key: string };

/** Imagem sem legenda exibível — candidata a grid (imagem+caption fica no MessageBubble). */
function isGroupableImage(msg: ChatMessage): boolean {
    if (msg.type !== 'image' || !msg.media?.url) return false;
    return !getMessageCaption(msg, msg.sender === 'me')?.trim();
}

/** Agrupa imagens consecutivas do mesmo remetente. Legenda = `getMessageCaption` (mesmo critério do bubble). */
function groupItems(items: MessageListItem[]): RenderItem[] {
    const result: RenderItem[] = [];
    let imageBuffer: ChatMessage[] = [];
    let bufferSender: ChatMessage['sender'] | null = null;

    const flushBuffer = () => {
        if (imageBuffer.length >= 2) {
            result.push({
                kind: 'image-group',
                messages: imageBuffer,
                isMe: bufferSender === 'me',
                key: `img-group-${stableMessageKey(imageBuffer[0])}`,
            });
        } else if (imageBuffer.length === 1) {
            result.push({ kind: 'message', message: imageBuffer[0], key: stableMessageKey(imageBuffer[0]) });
        }
        imageBuffer = [];
        bufferSender = null;
    };

    for (const item of items) {
        if (item.type === 'date') {
            flushBuffer();
            result.push({ kind: 'date', date: item.date!, key: `date-${item.date}` });
            continue;
        }

        const msg = item.message;
        if (!msg) continue;

        if (!isGroupableImage(msg)) {
            flushBuffer();
            result.push({ kind: 'message', message: msg, key: stableMessageKey(msg) });
            continue;
        }

        if (bufferSender === msg.sender) {
            imageBuffer.push(msg);
        } else {
            flushBuffer();
            imageBuffer = [msg];
            bufferSender = msg.sender;
        }
    }

    flushBuffer();
    return result;
}

function ListSpinner({ size }: { size: 'sm' | 'md' }) {
    const dim = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
    return <div className={`${dim} border-2 border-primary border-t-transparent rounded-full animate-spin`} aria-hidden />;
}

export const MessageList = React.memo(function MessageList({
    messages,
    conversationName,
    conversationAvatar,
    myAvatar,
    chatPlatform = 'whatsapp',
    isLoading,
    isPopup,
    scrollContainerRef,
    messagesEndRef,
    onScroll,
    onScrollToMessage,
    onContextMenu,
    highlightedMessageId,
    isLoadingOlder,
    hasMoreMessages = true,
}: MessageListProps) {
    const renderItems = useMemo(() => groupItems(messages), [messages]);
    const [showSpinner, setShowSpinner] = useState(false);

    useEffect(() => {
        if (!isLoading) {
            setShowSpinner(false);
            return;
        }
        const timer = setTimeout(() => setShowSpinner(true), 150);
        return () => clearTimeout(timer);
    }, [isLoading]);

    return (
        <div
            ref={scrollContainerRef}
            onScroll={onScroll}
            className={`min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain custom-scrollbar ${isPopup ? 'p-3 space-y-2' : 'p-4 space-y-2'} bg-chat-window-bg relative`}
        >
            {isLoadingOlder && (
                <div className="flex items-center justify-center py-3 sticky top-0 z-10 bg-background/80 backdrop-blur-sm rounded-lg">
                    <div className="flex items-center gap-2 text-secondary">
                        <ListSpinner size="sm" />
                        <span className="text-caption">Carregando mensagens anteriores...</span>
                    </div>
                </div>
            )}

            {!hasMoreMessages && messages.length > 0 && !isLoading && (
                <div className="flex justify-center py-2">
                    <span className="text-caption text-muted font-medium pb-2">
                        Início da conversa
                    </span>
                </div>
            )}

            {showSpinner && messages.length === 0 && (
                <div className="flex items-center justify-center py-8">
                    <div className="flex items-center gap-3 text-secondary">
                        <ListSpinner size="md" />
                        <span className="text-body-sm">Carregando mensagens...</span>
                    </div>
                </div>
            )}

            {messages.length > 0 && renderItems.map((item) => {
                if (item.kind === 'date') {
                    return (
                        <div key={item.key} className="flex justify-center my-4">
                            <span className="bg-surface text-secondary chat-date-sep px-4 py-1 rounded-full">
                                {item.date}
                            </span>
                        </div>
                    );
                }

                if (item.kind === 'image-group') {
                    const lastMsg = item.messages[item.messages.length - 1];
                    return (
                        <div key={item.key} className={`flex ${item.isMe ? 'justify-end' : 'justify-start'}`}>
                            <div className="flex min-w-0 max-w-[85%] flex-col sm:max-w-[75%]">
                                <div className={`rounded-2xl shadow-sm overflow-hidden ${item.isMe
                                    ? 'bg-primary rounded-br-none'
                                    : 'bg-surface rounded-bl-none'
                                }`}>
                                    <ImageGroup messages={item.messages} isMe={item.isMe} />
                                    <div className={`px-3 py-1 flex justify-end items-center gap-1 chat-msg-time ${item.isMe ? 'text-primary-foreground/70' : 'text-muted'}`}>
                                        <FormatTime date={lastMsg.timestamp} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                }

                if (item.message.type === 'system') {
                    return (
                        <div key={item.key} className="flex justify-center px-2 my-3 sm:my-4">
                            <p
                                role="status"
                                className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border-subtle bg-card px-3 py-1 text-caption text-secondary shadow-xs sm:gap-2 sm:px-4 sm:py-1.5"
                            >
                                <AppIcon name="user" className="size-3.5 shrink-0 text-muted-foreground sm:size-4" aria-hidden="true" />
                                <span>{item.message.text}</span>
                            </p>
                        </div>
                    );
                }

                return (
                    <MessageBubble
                        key={item.key}
                        msg={item.message}
                        conversationName={conversationName}
                        conversationAvatar={conversationAvatar}
                        myAvatar={myAvatar}
                        chatPlatform={chatPlatform}
                        onScrollToMessage={onScrollToMessage}
                        onContextMenu={(e) => onContextMenu(e, item.message)}
                        isHighlighted={highlightedMessageId === item.message.id}
                    />
                );
            })}
            <div ref={messagesEndRef} />
        </div>
    );
});
